import {
  Bell,
  CalendarDays,
  FileText,
  Import,
  MoreHorizontal,
  PanelLeft,
  RefreshCw,
} from "lucide-react";
import { useLocaleState, useTranslate } from "ra-core";
import { useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { formatDate } from "@/lib/dates";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import { formatBadgeCount } from "../dashboard/desk/format";
import { HomeHeaderTitle } from "../dashboard/home/HomeHeader";
import { HomeViewToggle } from "../dashboard/home/HomeViewToggle";
import { ChangelogPage } from "../misc/ChangelogPage";
import { ImportPage } from "../misc/ImportPage";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { GlobalSearchButton } from "../search/GlobalSearchButton";
import { useDeskShell } from "./desk/DeskShellContext";
import { ICON_STROKE, useNavLabel } from "./desk/navHelpers";
import { buildDeskNav, isItemActive } from "./desk/navConfig";
import type { DueItem } from "./desk/shellModel";
import { useMyTasks, useTodayAgendaCount } from "./desk/useShellData";
import { useRefreshData } from "./useRefreshData";

const MAX_BELL_ITEMS = 8;

/** Other pages: a small muted section name (pages render their own title). */
const SectionName = () => {
  const { pathname } = useLocation();
  const labelOf = useNavLabel();
  const { noteStatuses, dealStages, dealPipelineStatuses } =
    useConfigurationContext();
  const item = useMemo(
    () =>
      buildDeskNav({ noteStatuses, dealStages, dealPipelineStatuses }).find(
        (entry) => entry.to !== "/" && isItemActive(entry, pathname),
      ),
    [noteStatuses, dealStages, dealPipelineStatuses, pathname],
  );
  return item ? (
    <p className="desk-header__section">{labelOf(item.label)}</p>
  ) : null;
};

const useDueLabel = () => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  return (item: DueItem) =>
    item.state === "overdue"
      ? translate("desk.shell.notifications.overdue")
      : item.state === "today"
        ? translate("desk.shell.notifications.today")
        : formatDate(item.due, locale, {
            weekday: "short",
            day: "numeric",
            month: "short",
          });
};

/** Bell: my overdue / today / next-7-days tasks (contact + team tasks). */
const NotificationsButton = () => {
  const translate = useTranslate();
  const { items, overdue, dueToday } = useMyTasks();
  const dueLabel = useDueLabel();
  const label = translate("desk.shell.notifications.title");
  const visible = items.slice(0, MAX_BELL_ITEMS);
  return (
    <Popover>
      <PopoverTrigger
        className="desk-icon-btn desk-press"
        aria-label={label}
        title={label}
      >
        <Bell aria-hidden className="size-4" strokeWidth={ICON_STROKE} />
        {overdue + dueToday > 0 ? (
          <span className="desk-header__dot" aria-hidden />
        ) : null}
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={8} className="desk-bell w-80 p-0">
        <div className="desk-bell__head">
          <span className="desk-bell__title">{label}</span>
          <Link to="/team_tasks" className="desk-bell__all">
            {translate("desk.shell.notifications.see_all")}
          </Link>
        </div>
        {visible.length ? (
          <ul className="desk-bell__list">
            {visible.map((item) => (
              <li key={item.id}>
                <Link to={item.href} className="desk-bell__item">
                  <span
                    className="desk-bell__state"
                    data-state={item.state}
                    aria-hidden
                  />
                  <span className="desk-bell__text">{item.title}</span>
                  <span className="desk-bell__due" data-state={item.state}>
                    {dueLabel(item)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="desk-bell__empty">
            {translate("desk.shell.notifications.empty")}
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
};

const AgendaButton = () => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const count = useTodayAgendaCount();
  const badge = formatBadgeCount(count);
  const label = translate("desk.shell.agenda_today", { smart_count: count });
  return (
    <button
      type="button"
      className="desk-icon-btn desk-press"
      aria-label={label}
      title={label}
      onClick={() => navigate("/agenda")}
    >
      <CalendarDays aria-hidden className="size-4" strokeWidth={ICON_STROKE} />
      {badge ? (
        <span className="desk-header__badge" aria-hidden>
          {badge}
        </span>
      ) : null}
    </button>
  );
};

/** "…": the remaining actions of the old top bar. */
const MoreMenu = () => {
  const translate = useTranslate();
  const { refresh, isRefreshing } = useRefreshData();
  const { collapsed, toggle } = useDeskShell();
  const label = translate("desk.shell.more_actions");
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="desk-icon-btn desk-header__more desk-press"
        aria-label={label}
        title={label}
      >
        <MoreHorizontal
          aria-hidden
          className="size-4"
          strokeWidth={ICON_STROKE}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="w-56">
        <DropdownMenuItem
          onClick={() => void refresh()}
          disabled={isRefreshing}
        >
          <RefreshCw strokeWidth={ICON_STROKE} />
          {translate("desk.shell.refresh")}
        </DropdownMenuItem>
        <DropdownMenuItem
          render={
            <Link to={ImportPage.path} className="flex items-center gap-2" />
          }
        >
          <Import strokeWidth={ICON_STROKE} />
          {translate("crm.header.import_data")}
        </DropdownMenuItem>
        <DropdownMenuItem
          render={
            <Link to={ChangelogPage.path} className="flex items-center gap-2" />
          }
        >
          <FileText strokeWidth={ICON_STROKE} />
          {translate("crm.changelog.title")}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={toggle}>
          <PanelLeft strokeWidth={ICON_STROKE} />
          {translate(collapsed ? "desk.shell.expand" : "desk.shell.collapse")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/** Top of the desktop sheet, on every page. */
const Header = () => {
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  return (
    <header className="desk-header">
      <div className="desk-header__left">
        {isHome ? <HomeHeaderTitle /> : <SectionName />}
      </div>
      <div className="desk-header__actions">
        {isHome ? <HomeViewToggle /> : null}
        <AgendaButton />
        <NotificationsButton />
        <GlobalSearchButton variant="desk" />
        <MoreMenu />
      </div>
    </header>
  );
};

export default Header;
